import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import QrScanner from 'qr-scanner';

import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { API_ENDPOINTS, API_HEADERS } from '../services/constants';
import '../styles/QrKodOkuma.css';

interface AracBilgisi {
  plaka: string;
  uyeno: string;
  uyeid: string;
  sofor_adi: string;
  sofor_soyadi: string;
}

interface QrGecmis {
  plaka: string;
  tarih: Date;
  basarili: boolean;
}

const QrKodOkuma: React.FC = () => {
  const navigate = useNavigate();
  const [manuelPlaka, setManuelPlaka] = useState<string>('');
  const [aracBilgisi, setAracBilgisi] = useState<AracBilgisi | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [scannerActive, setScannerActive] = useState<boolean>(false);
  const [sorguPlaka, setSorguPlaka] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [qrGecmis, setQrGecmis] = useState<QrGecmis[]>([]);
  const [showGecmis, setShowGecmis] = useState<boolean>(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);

  useEffect(() => {
    // LocalStorage'dan geçmişi yükle
    const savedGecmis = localStorage.getItem('qrGecmis');
    if (savedGecmis) {
      try {
        const parsed = JSON.parse(savedGecmis);
        setQrGecmis(parsed.map((item: any) => ({
          ...item,
          tarih: new Date(item.tarih)
        })));
      } catch (e) {
        console.error('Geçmiş yüklenemedi:', e);
      }
    }
    
    // Cleanup fonksiyonu
    return () => {
      cleanupScanner();
    };
  }, []);
  
  const qrGecmiseEkle = (plaka: string, basarili: boolean) => {
    const yeniGecmis: QrGecmis = {
      plaka,
      tarih: new Date(),
      basarili
    };
    
    const guncelGecmis = [yeniGecmis, ...qrGecmis].slice(0, 10); // Son 10 kayıt
    setQrGecmis(guncelGecmis);
    localStorage.setItem('qrGecmis', JSON.stringify(guncelGecmis));
  };

  const initializeScanner = async () => {
    if (scannerActive) {
      cleanupScanner();
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      setScannerActive(true);

      if (!videoRef.current) {
        throw new Error('Video elementi bulunamadı');
      }

      // QR Scanner'ı başlat
      scannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          console.log('QR kod okundu:', result.data);
          
          // Scanner'ı durdur
          cleanupScanner();
          
          // Plaka bilgisini çıkar
          let plaka = '';
          const qrData = result.data.trim();
          
          try {
            // Önce JSON formatını kontrol et
            let qrJson;
            try {
              // Eksik başlangıç parantezi varsa düzelt
              let jsonString = qrData;
              if (jsonString.startsWith('"') || jsonString.includes('"id"') || jsonString.includes('"plaka"')) {
                // JSON formatı gibi görünüyor ama başlangıç { eksik olabilir
                if (!jsonString.startsWith('{')) {
                  // Eğer sadece içerik varsa, başlangıç { ekle
                  if (jsonString.includes('"id"') || jsonString.includes('"plaka"')) {
                    jsonString = '{' + jsonString;
                  }
                }
                // Eksik kapanış parantezi varsa ekle
                if (!jsonString.endsWith('}')) {
                  jsonString = jsonString + '}';
                }
                qrJson = JSON.parse(jsonString);
              } else {
                // JSON formatı değil, string olarak işle
                throw new Error('Not JSON format');
              }
            } catch (jsonError) {
              // JSON parse edilemedi, string format olarak işle
              qrJson = null;
            }
            
            if (qrJson && typeof qrJson === 'object' && qrJson.plaka) {
              // JSON formatında plaka var
              plaka = qrJson.plaka.toUpperCase();
              console.log('JSON formatından plaka çıkarıldı:', plaka);
            } else {
              // String format - eski formatları kontrol et
              plaka = qrData.toUpperCase();
              
              // TAKSI_QR formatını kontrol et: TAKSI_QR:ID-PLATE
              if (plaka.startsWith('TAKSI_QR:')) {
                const qrContent = plaka.replace('TAKSI_QR:', '');
                // ID-PLATE formatında, plakayı al (tire'den sonraki kısım)
                if (qrContent.includes('-')) {
                  const parts = qrContent.split('-');
                  // Son kısım plaka olmalı
                  plaka = parts[parts.length - 1];
                } else {
                  // Sadece plaka varsa direkt kullan
                  plaka = qrContent;
                }
              }
              // Diğer formatları kontrol et
              else if (plaka.includes('=')) {
                plaka = plaka.split('=')[1].split('&')[0];
              } else if (plaka.includes(':')) {
                const afterColon = plaka.split(':')[1];
                // Eğer tire içeriyorsa, tire'den sonraki kısmı al (plaka)
                if (afterColon.includes('-')) {
                  const parts = afterColon.split('-');
                  plaka = parts[parts.length - 1];
                } else {
                  plaka = afterColon;
                }
              }
            }
            
            if (!plaka || plaka.length === 0) {
              throw new Error('Plaka bilgisi çıkarılamadı');
            }
            
            setSorguPlaka(plaka);
            setSuccessMessage(`QR kod başarıyla okundu: ${plaka}`);
            setTimeout(() => setSuccessMessage(''), 3000);
            
            // Haptic feedback (mobil cihazlarda)
            if (navigator.vibrate) {
              navigator.vibrate(200);
            }
            
            getAracBilgileri(plaka);
          } catch (error: any) {
            console.error('QR kod parse hatası:', error);
            setError(`QR kod okunamadı: ${error?.message || 'Geçersiz format'}`);
            setAracBilgisi(null);
          }
        },
        {
          preferredCamera: 'environment', // Arka kamera
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 5,
        }
      );

      // Kamera başlat
      await scannerRef.current.start();
      
    } catch (error: any) {
      console.error('QR Scanner başlatma hatası:', error);
      setError(`QR Scanner başlatılamadı: ${error?.message || 'Kamera erişimi reddedildi'}`);
      setScannerActive(false);
    }
  };

  const cleanupScanner = () => {
    try {
      if (scannerRef.current) {
        scannerRef.current.stop();
        scannerRef.current.destroy();
        scannerRef.current = null;
      }
      setScannerActive(false);
    } catch (error: any) {
      console.error('Scanner cleanup hatası:', error);
      setScannerActive(false);
    }
  };

  const getAracBilgileri = async (plaka: string) => {
    if (!plaka.trim()) {
      setError('Lütfen geçerli bir plaka girin.');
      return;
    }

    setIsLoading(true);
    setError('');
    setAracBilgisi(null);

    try {
      // Form Data formatında gönder (dokümantasyona göre)
      const formData = new FormData();
      formData.append('method', 'getAracBilgileri');
      formData.append('plaka', plaka.toUpperCase());

      const response = await fetch(API_ENDPOINTS.WEBSERVICE, {
        method: 'POST',
        body: formData,
      });

      // Önce response text'i al
      const responseText = await response.text();
      
      // JSON parse et
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON Parse Hatası:', parseError);
        console.error('Response Text:', responseText);
        throw new Error('Sunucudan geçersiz yanıt alındı. Lütfen tekrar deneyin.');
      }

      // Dokümantasyona göre yanıt formatı: arac_sahibi ve soforler
      if (result.success) {
        if (result.arac_sahibi) {
          // Plaka veya Üye ID ile sorgulama - arac_sahibi ve soforler döner
          const aracSahibi = result.arac_sahibi;
          // İlk şoförü al (varsa) veya araç sahibini kullan
          const sofor = result.soforler && result.soforler.length > 0 
            ? result.soforler[0] 
            : aracSahibi;
          
          setAracBilgisi({
            plaka: aracSahibi.plaka || plaka.toUpperCase(),
            uyeno: aracSahibi.uyeno || '',
            uyeid: aracSahibi.uyeid || '',
            sofor_adi: sofor.ADI || sofor.adi || aracSahibi.adi || '',
            sofor_soyadi: sofor.SOYADI || sofor.soyadi || aracSahibi.soyadi || ''
          });
          setManuelPlaka('');
          setSorguPlaka('');
          setSuccessMessage('Araç bilgileri başarıyla yüklendi!');
          setTimeout(() => setSuccessMessage(''), 3000);
          qrGecmiseEkle(plaka.toUpperCase(), true);
        } else if (result.kullanicilar && result.kullanicilar.length > 0) {
          // Ad, Soyad veya Kart Numarası ile sorgulama - kullanicilar array döner
          const kullanici = result.kullanicilar[0];
          setAracBilgisi({
            plaka: kullanici.plaka || plaka.toUpperCase(),
            uyeno: kullanici.UYENO || '',
            uyeid: kullanici.uyeid || '',
            sofor_adi: kullanici.ADI || '',
            sofor_soyadi: kullanici.SOYADI || ''
          });
          setManuelPlaka('');
          setSorguPlaka('');
          setSuccessMessage('Araç bilgileri başarıyla yüklendi!');
          setTimeout(() => setSuccessMessage(''), 3000);
          qrGecmiseEkle(plaka.toUpperCase(), true);
        } else {
          setError(result.message || 'Bu plakaya ait araç bulunamadı.');
          setAracBilgisi(null);
          qrGecmiseEkle(plaka.toUpperCase(), false);
        }
      } else {
        setError(result.message || 'Bu plakaya ait araç bulunamadı.');
        setAracBilgisi(null);
        qrGecmiseEkle(plaka.toUpperCase(), false);
      }
    } catch (error: any) {
      console.error('Araç bilgileri alma hatası:', error);
      setError(`Bağlantı hatası: ${error?.message || 'Bilinmeyen hata'}`);
      setAracBilgisi(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManuelArama = () => {
    if (manuelPlaka.trim()) {
      setSorguPlaka(manuelPlaka.toUpperCase());
      getAracBilgileri(manuelPlaka);
    }
  };

  const handleDegerlendirmeYap = () => {
    if (aracBilgisi) {
      const aracData = encodeURIComponent(JSON.stringify(aracBilgisi));
      navigate(`/arac-degerlendirme?arac=${aracData}`);
    }
  };

  return (
    <div className="app-container">
      <Header title="QR Kod Okuma" />
      <div className="page-content">
        <div className="qr-container">
          <div className="qr-header">
            <h2>Araç Değerlendirme</h2>
            <p>QR kod okutun veya plaka numarasını girin</p>
            {qrGecmis.length > 0 && (
              <button 
                className="gecmis-button"
                onClick={() => setShowGecmis(!showGecmis)}
              >
                <i className={`fas ${showGecmis ? 'fa-chevron-up' : 'fa-history'}`}></i>
                {showGecmis ? 'Geçmişi Gizle' : `Geçmiş (${qrGecmis.length})`}
              </button>
            )}
          </div>
          
          {/* QR Geçmişi */}
          {showGecmis && qrGecmis.length > 0 && (
            <div className="qr-gecmis">
              <h4>Son Okunan QR Kodlar</h4>
              <div className="gecmis-listesi">
                {qrGecmis.map((item, index) => (
                  <div key={index} className={`gecmis-item ${item.basarili ? 'basarili' : 'basarisiz'}`}>
                    <div className="gecmis-plaka">
                      <i className={`fas ${item.basarili ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                      <span>{item.plaka}</span>
                    </div>
                    <div className="gecmis-tarih">
                      {item.tarih.toLocaleString('tr-TR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <button 
                      className="gecmis-tekrar-ara"
                      onClick={() => {
                        setManuelPlaka(item.plaka);
                        setShowGecmis(false);
                        getAracBilgileri(item.plaka);
                      }}
                    >
                      <i className="fas fa-redo"></i>
                    </button>
                  </div>
                ))}
              </div>
              <button 
                className="gecmis-temizle"
                onClick={() => {
                  setQrGecmis([]);
                  localStorage.removeItem('qrGecmis');
                  setShowGecmis(false);
                }}
              >
                <i className="fas fa-trash"></i>
                Geçmişi Temizle
              </button>
            </div>
          )}

          {/* QR Kod Okuma Bölümü */}
          <div className="qr-section">
            <div className="qr-controls">
              <button 
                className={`qr-scan-button ${scannerActive ? 'active' : ''}`}
                onClick={initializeScanner}
                disabled={isLoading}
              >
                <i className={`fas ${scannerActive ? 'fa-stop' : 'fa-qrcode'}`}></i>
                {scannerActive ? 'QR Okumayı Durdur' : 'QR Kod Okut'}
              </button>
            </div>
            
            {/* QR Scanner Video */}
            <div className="qr-scanner-container">
              <video
                ref={videoRef}
                className={`qr-video ${scannerActive ? 'active' : 'hidden'}`}
                playsInline
                muted
              />
              {scannerActive && (
                <p className="scan-instruction">QR kodu kamera karşısına getirin</p>
              )}
            </div>
          </div>

          {/* Sorgu Durumu */}
          {sorguPlaka && (
            <div className="sorgu-durumu">
              <div className="sorgu-plaka">
                <i className="fas fa-search"></i>
                <span>Sorgulanan: <strong>{sorguPlaka}</strong></span>
              </div>
            </div>
          )}

          {/* Manuel Plaka Girişi */}
          <div className="manuel-section">
            <h3>Manuel Plaka Girişi</h3>
            <div className="manuel-input">
              <input
                type="text"
                placeholder="Plaka numarasını girin (örn: 34ABC123)"
                value={manuelPlaka}
                onChange={(e) => setManuelPlaka(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleManuelArama()}
                maxLength={10}
              />
              <button 
                onClick={handleManuelArama}
                disabled={!manuelPlaka.trim() || isLoading}
                className="search-button"
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Aranıyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search"></i>
                    Ara
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Başarı Mesajı */}
          {successMessage && (
            <div className="success-message">
              <i className="fas fa-check-circle"></i>
              <p>{successMessage}</p>
            </div>
          )}

          {/* Hata Mesajı */}
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              <p>{error}</p>
            </div>
          )}

          {/* Araç Bilgileri */}
          {aracBilgisi && (
            <div className="arac-bilgileri">
              <h3>Araç Bilgileri</h3>
              <div className="bilgi-card">
                <div className="bilgi-item">
                  <span className="label">Plaka:</span>
                  <span className="value">{aracBilgisi.plaka}</span>
                </div>
                <div className="bilgi-item">
                  <span className="label">Şoför:</span>
                  <span className="value">{aracBilgisi.sofor_adi} {aracBilgisi.sofor_soyadi}</span>
                </div>
                <div className="bilgi-item">
                  <span className="label">Üye No:</span>
                  <span className="value">{aracBilgisi.uyeno}</span>
                </div>
              </div>
              
              <button 
                className="degerlendirme-button"
                onClick={handleDegerlendirmeYap}
              >
                <i className="fas fa-star"></i>
                Değerlendirme Yap
              </button>
            </div>
          )}

          {/* Yükleniyor */}
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <p>Araç bilgileri alınıyor...</p>
            </div>
          )}
        </div>
      </div>
      <TabBar />
    </div>
  );
};

export default QrKodOkuma; 